/* eslint-disable react/prop-types */
import React, { useRef, useState, useCallback, memo, useEffect, useMemo } from 'react';
import { useSelector } from 'store';
import { reduxActions, useDispatch } from 'store';
import ChatWindow from 'components/containers/ChatWindow';
import { AssistantInfo } from 'components/containers/ChatBase';
import { useChatState } from 'hooks/useChatState';

import styles from 'theme/components/containers/DockedChatContainer/DockedChatContainer.module.scss';

// Stałe dla ograniczeń szerokości okna docked
const MIN_DOCKED_WIDTH = 300;
const MAX_DOCKED_WIDTH = 800;
const MAX_DOCKED_WIDTH_RATIO = 0.5;

interface DockedChatContainerProps {
  extraUrlParameters?: Record<string, string>;
  availableAssistants?: AssistantInfo[];
  hideQuickActions?: boolean;
}

const DockedChatContainer: React.FC<DockedChatContainerProps> = memo(({ extraUrlParameters, availableAssistants, hideQuickActions }) => {
  const dispatch = useDispatch();
  const { chatDockedWidth } = useSelector((state) => state.chat);
  
  const {
    shouldShowAgentAI,
    closeChat,
    toggleDock,
    toggleHorizontal,
    toggleResizable,
    toggleFullScreen
  } = useChatState();
  
  // Refs dla elementów DOM
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);
  
  // Stan lokalny dla płynnego resize'owania
  const [isResizing, setIsResizing] = useState(false);
  const [localWidth, setLocalWidth] = useState<number | null>(null);

  // Rozpocznij proces zmiany rozmiaru
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  // Obsługa przesuwania myszy podczas zmiany rozmiaru
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      // Anuluj oczekujące RAF, aby zapobiec kolejkowaniu
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Użyj RAF do ograniczenia aktualizacji do 60fps
      rafIdRef.current = requestAnimationFrame(() => {
        const newWidth = window.innerWidth - e.clientX;
        const maxWidth = Math.min(MAX_DOCKED_WIDTH, window.innerWidth * MAX_DOCKED_WIDTH_RATIO);
        const clampedWidth = Math.max(MIN_DOCKED_WIDTH, Math.min(maxWidth, newWidth));
        
        // Użyj lokalnego stanu podczas resize dla płynnej wydajności
        setLocalWidth(clampedWidth);
      });
    },
    [isResizing]
  );

  // Zakończ proces zmiany rozmiaru i zapisz wynik
  const handleMouseUp = useCallback(() => {
    // Anuluj oczekujące RAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }

    // Zapisz końcową szerokość do Redux
    if (localWidth !== null) {
      dispatch(reduxActions.setChatDockedWidth(localWidth));
      setLocalWidth(null);
    }

    setIsResizing(false);
  }, [localWidth, dispatch]);

  // Obsługa globalnych eventów podczas zmiany rozmiaru
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMoveWithCursor = (e: MouseEvent) => {
      // Wymuś kursor col-resize podczas całego procesu przeciągania
      document.body.style.cursor = 'col-resize';
      handleMouseMove(e);
    };

    // Dodaj globalne listenery
    document.addEventListener('mousemove', handleMouseMoveWithCursor);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp); // Obsługa gdy kursor opuści okno przeglądarki
    
    // Wymuś kursor i style na całym dokumencie
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'auto';

    return () => {
      // Usuń listenery
      document.removeEventListener('mousemove', handleMouseMoveWithCursor);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
      
      // Przywróć domyślne style
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      
      // Anuluj oczekujące RAF, aby zapobiec wyciekom pamięci
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Oblicz aktualną szerokość kontenera
  const containerWidth = useMemo(() => {
    return localWidth !== null && isResizing ? localWidth : chatDockedWidth;
  }, [localWidth, isResizing, chatDockedWidth]);

  // Style dla overlay podczas resize'owania
  const resizeOverlayStyle = useMemo(
    () => ({
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      cursor: 'col-resize' as const,
      zIndex: 10000,
      pointerEvents: 'auto' as const
    }),
    []
  );

  // Nie renderuj, jeśli agent nie powinien być widoczny
  if (!shouldShowAgentAI('docked')) return null;

  return (
    <>
      {/* Overlay podczas zmiany rozmiaru - przechwytuje eventy myszy */}
      {isResizing && (
        <div style={resizeOverlayStyle} onMouseUp={handleMouseUp} />
      )}
      
      <div 
        ref={containerRef}
        className={styles.dockedChatContainer}
        style={{ width: `${containerWidth}px` }}
      >
        {/* Handle do zmiany rozmiaru */}
        <div
          ref={resizeHandleRef}
          className={styles.resizeHandle}
          onMouseDown={handleMouseDown}
          title="Przeciągnij aby zmienić szerokość"
        />
        
        <ChatWindow
          variant="docked"
          onCloseChat={closeChat}
          onToggleDock={toggleDock}
          onToggleHorizontal={toggleHorizontal}
          onToggleResizable={toggleResizable}
          onToggleFullScreen={toggleFullScreen}
          showNavigationBar
          showDragHandle={false}
          extraUrlParameters={extraUrlParameters}
          availableAssistants={availableAssistants}
          hideQuickActions={hideQuickActions}
        />
      </div>
    </>
  );
});

DockedChatContainer.displayName = 'DockedChatContainer';

export default DockedChatContainer;

